// UC-1b: renders the question flow over the existing pure UC-1a logic in src/session/state.ts.
// No new state logic lives here, only DOM rendering and motion. Handles all three answer types
// (single, multi, pii), the "none of these" affordance, back/change-answer, a progress
// indicator, and question-to-question transitions with a slight option stagger.

import type { ConfigVersion, Question } from "../types";
import type { SessionCampaign } from "../campaign";
import {
  currentQuestion,
  isComplete,
  optOut,
  recordAnswer,
  startSession,
  type DiscoverySessionState,
  type PiiValue,
} from "../session/state";
import * as apiClient from "./api-client";
import { loadingHtml } from "./loading";
import { randomId } from "./random-id";

export interface QuestionFlowHost {
  onComplete: (session: DiscoverySessionState) => void;
  onOptOut: (session: DiscoverySessionState) => void;
}

/** Present only when the embed is running against a real Session API (`config-endpoint`
 * attribute). Local sequencing (src/session/state.ts) stays the source of truth for what the
 * visitor sees either way; this bundle just keeps the server's copy in lockstep, which is what
 * ResultScreen's /complete call needs to be correct. */
export interface SessionApiBundle {
  baseUrl: string;
  sessionId: string;
  /** Answers already recorded server-side for this sessionId (UC-1a extension 1a: same-device
   * resume). Replayed into the fresh local DiscoverySessionState at construction so the visitor
   * picks up where they left off instead of seeing question 1 again — the server already knows
   * this, so the client has to catch up rather than the other way around. */
  resumedAnswers?: { questionId: string; selectedOptionIds: string[]; isNoneOfThese: boolean; piiValue?: PiiValue }[];
  /** ADR-011: the question the server's adaptive planner has already decided comes next (from
   * `POST /session`'s own `nextQuestionId` — the same pending question read back on resume, not
   * recomputed). `null` only if a session somehow starts already complete. Source of truth for
   * what to show next in live mode; this component never falls back to computing it from `order`
   * itself, since the adaptive flow doesn't follow `order` (only the mock-data path does). */
  nextQuestionId: string | null;
  /** docs/plans/2026-10-08-options-from-attribute.md, Task 4: per-session options for any
   * optionsFromAttribute question this session has asked, keyed by question id. The downloaded
   * config has none for those questions, so these replace them when rendering. Updated from every
   * /answers response. */
  questionOptions?: Record<string, { id: string; label: string }[]>;
  /** docs/plans/2026-10-13-campaigns.md: the campaign POST /session resolved for this page, if
   * any. Passed through untouched to the result screen. */
  campaign?: SessionCampaign;
}

/** Label for the "none of these" affordance, rendered whenever a question allowsNone. The flag
 * alone drives it: authored options are never inferred to be the none option, even when they
 * carry no tag weights, since a zero-weight option is a real answer on discriminating and
 * attribute questions (src/session/state.ts's own comment on recordAnswer). Inferring it used to
 * hide the button on every question whose options were all tagged, and swallow a real answer
 * as "none" on questions where one wasn't. */
const NONE_OF_THESE_LABEL = "None of these";

interface ThinkingMessage {
  heading: string;
  note: string;
}

/** Between-question loading copy (not the final match loading). One is picked at random per wait.
 * Client-facing: no em dashes. */
const THINKING_MESSAGES: readonly ThinkingMessage[] = [
  { heading: "Hang tight, I'm working on it.", note: "Thinking through what you've told us so far&hellip;" },
  { heading: "Let me think about that.", note: "Considering your last answer&hellip;" },
  { heading: "Good to know.", note: "Working out what to ask you next&hellip;" },
  { heading: "One moment.", note: "Piecing together what you've shared&hellip;" },
  { heading: "Just a moment.", note: "Reading between the lines&hellip;" },
  { heading: "Thanks for that.", note: "Figuring out the most useful next question&hellip;" },
  { heading: "Let me mull that over.", note: "Connecting your answers so far&hellip;" },
  { heading: "Almost there.", note: "Choosing the right question for you&hellip;" },
];

export class QuestionFlow {
  private session: DiscoverySessionState;
  private viewingQuestionId: string | null = null;
  private direction: 1 | -1 = 1;
  private persisting = false;
  private lastThinkingIndex = -1;
  /** Live mode only (undefined in mock mode, where currentQuestion()'s order-based computation
   * stays authoritative): the question the server's planner says comes next, kept in lockstep with
   * every API response — set at construction from SessionApiBundle.nextQuestionId, then updated by
   * every submit(). */
  private pendingQuestionId?: string | null;
  /** Ids of questions actually shown to the visitor, in the order shown. The only thing Back can
   * reliably step through in live mode: the adaptive flow doesn't follow `order`, so there's no
   * fixed list to index into the way the fixed flow's `order` field allowed. Seeded from
   * resumedAnswers on construction (chronological, since the server appends to `answers` as they're
   * recorded); mock mode builds the same list as the visitor progresses, so Back works identically
   * in both modes. */
  private history: string[] = [];
  /** See SessionApiBundle.questionOptions. Empty in mock mode. */
  private questionOptions: Record<string, { id: string; label: string }[]> = {};

  constructor(
    private readonly container: HTMLElement,
    private readonly config: ConfigVersion,
    private readonly host: QuestionFlowHost,
    private readonly apiBundle?: SessionApiBundle
  ) {
    this.session = startSession(randomId(), config.versionId, new Date());
    for (const answer of apiBundle?.resumedAnswers ?? []) {
      const question = config.questions.find((q) => q.id === answer.questionId && q.active);
      if (!question) continue; // stale answer against a question no longer active in this config
      this.session = recordAnswer(
        this.session,
        question,
        { selectedOptionIds: answer.selectedOptionIds, isNoneOfThese: answer.isNoneOfThese, piiValue: answer.piiValue },
        new Date()
      );
      this.history.push(answer.questionId);
    }
    if (apiBundle) {
      this.pendingQuestionId = apiBundle.nextQuestionId;
      this.questionOptions = apiBundle.questionOptions ?? {};
    }
  }

  /** Every question lookup goes through here, so an optionsFromAttribute question renders with the
   * options this session was actually offered rather than the config's empty list. */
  private findQuestion(id: string): Question | null {
    const question = this.config.questions.find((q) => q.id === id);
    if (!question) return null;
    const offered = this.questionOptions[id];
    if (!question.optionsFromAttribute || !offered) return question;
    return { ...question, options: offered.map((o, order) => ({ id: o.id, label: o.label, order, tagWeights: [] })) };
  }

  mount(): void {
    if (this.isFlowComplete()) {
      this.host.onComplete(this.session);
      return;
    }
    this.trackHistory();
    this.render();
  }

  /** Live mode: the server's planner is the sole authority on completion (ADR-011 — a session can
   * reach answers_complete without every active question answered, so the old "every active
   * question answered" rule no longer applies). Mock mode has no server, so it keeps the original
   * rule. */
  private isFlowComplete(): boolean {
    if (this.apiBundle) return this.pendingQuestionId == null;
    return isComplete(this.session, this.config.questions, this.config.settings);
  }

  private activeQuestion(): Question | null {
    if (this.viewingQuestionId) {
      return this.findQuestion(this.viewingQuestionId);
    }
    const id = this.apiBundle ? this.pendingQuestionId : currentQuestion(this.session, this.config.questions)?.id;
    return id ? this.findQuestion(id) : null;
  }

  /** Appends the current forward-path question to `history` if it isn't already the most recent
   * entry. Called after mount() and after every successful submit() that doesn't complete the
   * flow — never while merely viewing (goTo), since viewing an earlier question to change its
   * answer shouldn't grow the list. */
  private trackHistory(): void {
    const question = this.activeQuestion();
    if (question && this.history[this.history.length - 1] !== question.id) {
      this.history.push(question.id);
    }
  }

  private goTo(question: Question, direction: 1 | -1): void {
    this.viewingQuestionId = question.id;
    this.direction = direction;
    this.render();
  }

  private goBack(): void {
    // Blocked while an answer POST (and possibly a selector call) is in flight — the thinking
    // overlay already covers the question visually, but the Back button sits outside it in the
    // progress/back row above, so it needs its own guard rather than relying on the overlay alone.
    if (this.persisting) return;
    const question = this.activeQuestion();
    if (!question) return;
    const index = this.history.indexOf(question.id);
    if (index <= 0) return;
    const previousId = this.history[index - 1];
    const previous = previousId ? this.findQuestion(previousId) : null;
    if (previous) this.goTo(previous, -1);
  }

  private submit(question: Question, selectedOptionIds: string[], isNoneOfThese: boolean, piiValue?: PiiValue): void {
    void this.runWithRetry(async () => {
      if (this.apiBundle) {
        const result = await apiClient.recordAnswer(
          this.apiBundle.baseUrl,
          this.apiBundle.sessionId,
          question.id,
          selectedOptionIds,
          isNoneOfThese,
          piiValue
        );
        // ADR-011 Section 5 re-planning: changing an answer earlier than the most recently shown
        // question can discard everything the server asked after it. Truncate the local
        // shown-question history to match, so Back and the progress indicator reflect what the
        // planner actually kept rather than a stale local list that still remembers discarded
        // questions.
        const index = this.history.indexOf(question.id);
        if (index !== -1) this.history = this.history.slice(0, index + 1);
        this.pendingQuestionId = result.nextQuestionId;
        this.questionOptions = result.questionOptions ?? {};
      }

      this.session = recordAnswer(this.session, question, { selectedOptionIds, isNoneOfThese, piiValue }, new Date());
      this.viewingQuestionId = null;
      this.direction = 1;

      if (this.isFlowComplete()) {
        this.host.onComplete(this.session);
        return;
      }
      this.trackHistory();
      this.render();
    });
  }

  private handleOptOut(): void {
    void this.runWithRetry(async () => {
      if (this.apiBundle) {
        await apiClient.optOut(this.apiBundle.baseUrl, this.apiBundle.sessionId);
      }
      this.session = optOut(this.session, new Date());
      this.host.onOptOut(this.session);
    });
  }

  /** Runs an action that may include a Session API call, showing a fail-closed retry affordance
   * over the current question on failure rather than silently letting the visitor continue past an
   * unpersisted answer (which would desync local sequencing from what ResultScreen's /complete
   * eventually reads back from the server). A retry re-runs the whole action, so success resumes
   * exactly where submit()/handleOptOut() would have gone had the call succeeded the first time. */
  private async runWithRetry(action: () => Promise<void>): Promise<void> {
    this.persisting = true;
    this.renderPersistingState();
    try {
      await action();
      this.clearPersistError();
    } catch (err) {
      // The banner is deliberately generic for the visitor, but the action covers the API call and
      // the local re-render after it, so without this a client-side throw is indistinguishable
      // from a network or server failure when debugging.
      console.error("question-flow: action failed, showing retry banner", err);
      this.renderPersistError(action);
    } finally {
      this.persisting = false;
    }
  }

  private renderPersistingState(): void {
    const body = this.container.querySelector<HTMLElement>(".bfc-flow-body");
    body?.classList.toggle("bfc-flow-body-busy", this.persisting);
    this.clearPersistError();
    this.clearThinkingOverlay(body);
    // Live mode only: a genuine selector call can be in flight here (measured 1.6-4.7s against the
    // real API, tasks C and D's build notes), long enough that a plain dimmed-question overlay reads
    // as stalled rather than working. Mock mode has no network round trip, so there's nothing to
    // explain, and this covers submit()/handleOptOut() alike since both route through here. A full
    // opaque overlay (not just the dimming above) rather than a line of text at the bottom of the
    // question, matching the same loading.ts treatment result-screen.ts uses while it
    // waits on /complete, so the two genuinely network-bound waits in this flow read as one
    // consistent "working on it" moment rather than two different affordances.
    if (this.persisting && this.apiBundle && body) {
      const overlay = document.createElement("div");
      overlay.className = "bfc-thinking-overlay bfc-fade-enter";
      const message = this.pickThinkingMessage();
      overlay.innerHTML = loadingHtml({
        label: "Considering the next question",
        heading: message.heading,
        note: message.note,
      });
      body.appendChild(overlay);
    }
  }

  /** A random between-questions message, never the same one twice in a row, so repeated waits
   * feel varied rather than canned. */
  private pickThinkingMessage(): ThinkingMessage {
    let index = Math.floor(Math.random() * THINKING_MESSAGES.length);
    if (index === this.lastThinkingIndex) index = (index + 1) % THINKING_MESSAGES.length;
    this.lastThinkingIndex = index;
    return THINKING_MESSAGES[index];
  }

  private clearThinkingOverlay(body?: HTMLElement | null): void {
    (body ?? this.container).querySelector(".bfc-thinking-overlay")?.remove();
  }

  private clearPersistError(): void {
    this.container.querySelector(".bfc-persist-error")?.remove();
  }

  private renderPersistError(retry: () => Promise<void>): void {
    const body = this.container.querySelector<HTMLElement>(".bfc-flow-body");
    body?.classList.remove("bfc-flow-body-busy");
    if (!body) return;
    this.clearPersistError();
    this.clearThinkingOverlay(body);

    const banner = document.createElement("div");
    banner.className = "bfc-persist-error bfc-alert bfc-alert--error";
    banner.innerHTML = `
      <p>Something went wrong saving your answer. Check your connection and try again.</p>
      <button type="button" class="bfc-btn bfc-continue">Retry</button>
    `;
    banner.querySelector("button")!.addEventListener("click", () => void this.runWithRetry(retry));
    body.appendChild(banner);
  }

  private render(): void {
    const question = this.activeQuestion();
    if (!question) return; // caller (embed.ts) is expected to have already routed to completion

    // ADR-011: there is no fixed question count to measure progress against in live mode (the
    // adaptive flow can stop anywhere between minQuestions and maxQuestions, and the pool is
    // larger than either), so this reads against maxQuestions — an upper bound, not a promise —
    // rather than the old "answered / total active questions" fraction, which would badly
    // undercount once the real pool grows past a handful of questions (checklist Section 8: Sarah's
    // pool is headed to 25-40). Applied uniformly in mock mode too, even though its fixed flow can
    // walk past maxQuestions (the dummy fixture has more active questions than its maxQuestions):
    // the bar caps at 100% and stays there rather than reading over 100%, a fine outcome for a
    // disposable preview harness (mock-data.ts) that isn't meant to model real pool sizing.
    const index = this.history.indexOf(question.id);
    const shown = index === -1 ? this.history.length + 1 : index + 1;
    const progressPct = Math.min(100, (shown / this.config.settings.maxQuestions) * 100);

    const outgoing = this.container.querySelector(".bfc-flow-body");
    const wrapper = document.createElement("div");
    wrapper.className = "bfc-flow";

    wrapper.innerHTML = `
      <div class="bfc-progress"><div class="bfc-progress-fill" style="width:${progressPct}%"></div></div>
      <button type="button" class="bfc-back" ${index <= 0 ? "disabled" : ""}>&larr; Back</button>
      <div class="bfc-flow-body"></div>
    `;

    const back = wrapper.querySelector<HTMLButtonElement>(".bfc-back")!;
    back.addEventListener("click", () => this.goBack());

    const body = wrapper.querySelector<HTMLElement>(".bfc-flow-body")!;
    if (this.apiBundle && this.viewingQuestionId) {
      // Only shown when revisiting an earlier answer in live mode, where re-planning (ADR-011
      // Section 5) genuinely can discard later questions. PLACEHOLDER COPY, not voice-reviewed
      // (docs/plans/2026-09-14-adaptive-question-selection-build.md, Task D: "that is a copy
      // question, so flag it for the voice review rather than inventing wording") — same
      // disposable-until-approved status as src/matching/fallback-copy.ts.
      const note = document.createElement("p");
      note.className = "bfc-replan-note bfc-alert bfc-alert--warning";
      note.textContent = "Changing this answer may change what we ask next.";
      body.appendChild(note);
    }
    body.appendChild(this.renderQuestionBody(question));

    this.container.innerHTML = "";
    this.container.appendChild(wrapper);

    this.animateIn(body, this.direction);
    void outgoing; // prior body is discarded with the old wrapper; nothing to animate out of DOM
  }

  private animateIn(el: HTMLElement, direction: 1 | -1): void {
    const distance = 16 * direction;
    el.animate(
      [
        { opacity: 0, transform: `translateX(${distance}px)` },
        { opacity: 1, transform: "translateX(0)" },
      ],
      { duration: this.durationMs("--bfc-motion-duration-base"), easing: this.easing(), fill: "both" }
    );
  }

  private durationMs(token: string): number {
    const raw = getComputedStyle(this.container).getPropertyValue(token).trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 220;
  }

  private easing(): string {
    return getComputedStyle(this.container).getPropertyValue("--bfc-motion-ease").trim() || "ease-out";
  }

  private renderQuestionBody(question: Question): HTMLElement {
    const el = document.createElement("div");
    const heading = document.createElement("p");
    heading.className = "bfc-question";
    heading.textContent = question.text;
    el.appendChild(heading);

    if (question.answerType === "pii") {
      el.appendChild(this.renderPiiQuestion(question));
    } else {
      el.appendChild(this.renderChoiceQuestion(question));
    }
    return el;
  }

  private renderPiiQuestion(question: Question): HTMLElement {
    const el = document.createElement("div");

    const firstNameInput = document.createElement("input");
    firstNameInput.type = "text";
    firstNameInput.className = "bfc-pii-input";
    firstNameInput.placeholder = "First name";
    firstNameInput.setAttribute("aria-label", "First name");
    el.appendChild(firstNameInput);

    const lastNameInput = document.createElement("input");
    lastNameInput.type = "text";
    lastNameInput.className = "bfc-pii-input";
    lastNameInput.placeholder = "Last name";
    lastNameInput.setAttribute("aria-label", "Last name");
    el.appendChild(lastNameInput);

    const emailInput = document.createElement("input");
    emailInput.type = "email";
    emailInput.className = "bfc-pii-input bfc-email-input";
    emailInput.placeholder = "you@example.com";
    emailInput.setAttribute("aria-label", "Email address");
    el.appendChild(emailInput);

    const continueBtn = document.createElement("button");
    continueBtn.type = "button";
    continueBtn.className = "bfc-btn bfc-continue";
    continueBtn.textContent = "Continue";
    continueBtn.disabled = true;

    const updateContinueState = () => {
      const allFilled = firstNameInput.value.trim() !== "" && lastNameInput.value.trim() !== "" && emailInput.value.trim() !== "";
      continueBtn.disabled = !allFilled || !emailInput.checkValidity();
    };
    for (const input of [firstNameInput, lastNameInput, emailInput]) {
      input.addEventListener("input", updateContinueState);
    }

    continueBtn.addEventListener("click", () => {
      // PII routes to ContactCapture only, never into answersJson (src/session/state.ts,
      // toAnswersJson). This preview has no ContactCapture to route it to; recordAnswer here just
      // advances the flow the same way the real orchestration layer will once it exists.
      const piiValue: PiiValue = {
        firstName: firstNameInput.value.trim(),
        lastName: lastNameInput.value.trim(),
        email: emailInput.value.trim(),
      };
      this.submit(question, [], false, piiValue);
    });

    const actions = document.createElement("div");
    actions.className = "bfc-pii-actions";
    actions.appendChild(continueBtn);

    if (this.config.settings.requireEmailToReveal) {
      const optOutBtn = document.createElement("button");
      optOutBtn.type = "button";
      optOutBtn.className = "bfc-optout";
      optOutBtn.textContent = "I'd rather not, take me to Find a Board Member";
      optOutBtn.addEventListener("click", () => this.handleOptOut());
      actions.appendChild(optOutBtn);
    }
    el.appendChild(actions);

    const privacyNote = document.createElement("p");
    privacyNote.className = "bfc-privacy-note";
    const privacyPolicyUrl = this.config.settings.privacyPolicyUrl;
    privacyNote.innerHTML = privacyPolicyUrl
      ? `We'll only use this to send your recommendation. See our <a href="${privacyPolicyUrl}" target="_blank" rel="noopener">privacy policy</a>.`
      : "We'll only use this to send your recommendation.";
    el.appendChild(privacyNote);

    return el;
  }

  private renderChoiceQuestion(question: Question): HTMLElement {
    const el = document.createElement("div");
    const list = document.createElement("div");
    list.className = "bfc-options";

    const selectable = [...question.options].sort((a, b) => a.order - b.order);
    const selected = new Set<string>();

    const finishMulti = () => {
      continueBtn.disabled = selected.size === 0;
    };

    selectable.forEach((option, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bfc-option";
      btn.textContent = option.label;
      btn.setAttribute("aria-pressed", "false");
      btn.style.animationDelay = `calc(var(--bfc-motion-stagger-step) * ${i})`;
      btn.classList.add("bfc-fade-enter");

      btn.addEventListener("click", () => {
        if (question.answerType === "single") {
          this.submit(question, [option.id], false);
          return;
        }
        if (selected.has(option.id)) {
          selected.delete(option.id);
          btn.setAttribute("aria-pressed", "false");
        } else {
          selected.add(option.id);
          btn.setAttribute("aria-pressed", "true");
        }
        finishMulti();
      });

      list.appendChild(btn);
    });

    el.appendChild(list);

    let continueBtn: HTMLButtonElement;
    if (question.answerType === "multi") {
      continueBtn = document.createElement("button");
      continueBtn.type = "button";
      continueBtn.className = "bfc-btn bfc-continue";
      continueBtn.textContent = "Continue";
      continueBtn.disabled = true;
      continueBtn.style.marginTop = "var(--bfc-space-md)";
      continueBtn.addEventListener("click", () => {
        this.submit(question, Array.from(selected), false);
      });
      el.appendChild(continueBtn);
    }

    if (question.allowsNone) {
      const noneBtn = document.createElement("button");
      noneBtn.type = "button";
      noneBtn.className = "bfc-option bfc-option-none bfc-fade-enter";
      noneBtn.style.animationDelay = `calc(var(--bfc-motion-stagger-step) * ${selectable.length})`;
      noneBtn.textContent = NONE_OF_THESE_LABEL;
      noneBtn.addEventListener("click", () => this.submit(question, [], true));
      list.appendChild(noneBtn);
    }

    return el;
  }
}
